import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home, Wrench, MailCheck } from "lucide-react";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("consumer");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", newsletter_opt_in: true });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const land = (r) => (r === "admin" ? "/admin" : r === "provider" ? "/provider" : "/dashboard");
  const routeAfterAuth = (u) => (u.must_change_password ? "/change-password" : land(u.role));

  const forgot = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: form.email });
      setForgotSent(true);
      toast.success(data.message);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register({ name: form.name, email: form.email, password: form.password, role, company: form.company, newsletter_opt_in: form.newsletter_opt_in });
      toast.success(`Welcome, ${u.name}`);
      navigate(routeAfterAuth(u));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const demo = async (email, password) => {
    setBusy(true); setError("");
    try {
      const u = await login(email, password);
      navigate(routeAfterAuth(u));
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-black/5 bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
          <h1 className="font-serif text-4xl text-emerald-950">{mode === "login" ? "Welcome back" : mode === "register" ? "Join PlantFlow" : "Reset password"}</h1>
          <p className="mt-2 text-stone-500 text-sm">{mode === "login" ? "Sign in to your grove." : mode === "register" ? "Create your account to begin." : "Enter your email and we'll send a reset link."}</p>

          {mode === "forgot" ? (
            forgotSent ? (
              <div data-testid="forgot-sent" className="mt-6 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-6 text-center">
                <MailCheck className="mx-auto h-8 w-8 text-emerald-700" />
                <p className="mt-3 text-sm text-emerald-950">If an account exists for <b>{form.email}</b>, a password reset link is on its way. The link expires in 1 hour.</p>
                <Button data-testid="back-to-login" onClick={() => { setMode("login"); setForgotSent(false); setError(""); }} className="mt-5 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">Back to sign in</Button>
              </div>
            ) : (
              <form onSubmit={forgot} className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Email</Label>
                  <Input data-testid="forgot-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 rounded-xl" placeholder="you@email.com" />
                </div>
                {error && <p data-testid="auth-error" className="text-sm text-red-600">{error}</p>}
                <Button data-testid="forgot-submit" type="submit" disabled={busy} className="w-full rounded-full h-12 bg-emerald-900 hover:bg-emerald-800 text-white">
                  {busy ? "Sending…" : "Send reset link"}
                </Button>
                <button type="button" data-testid="forgot-cancel" onClick={() => { setMode("login"); setError(""); }} className="text-sm text-stone-500 hover:text-emerald-800 w-full text-center">Back to sign in</button>
              </form>
            )
          ) : (
          <>
          {mode === "register" && (
            <div className="grid grid-cols-2 gap-3 mt-6">
              {[["consumer", "Homeowner", Home], ["provider", "Landcare Crew", Wrench]].map(([val, label, Icon]) => (
                <button key={val} type="button" data-testid={`role-${val}`} onClick={() => setRole(val)}
                  className={`rounded-xl border-2 p-4 text-left transition-colors ${role === val ? "border-emerald-700 bg-emerald-50" : "border-stone-200 hover:border-stone-300"}`}>
                  <Icon className={`w-5 h-5 ${role === val ? "text-emerald-700" : "text-stone-400"}`} />
                  <div className="mt-2 text-sm font-semibold text-emerald-950">{label}</div>
                </button>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Name</Label>
                <Input data-testid="auth-name" value={form.name} onChange={set("name")} required className="mt-1.5 rounded-xl" placeholder="Jane Green" />
              </div>
            )}
            {mode === "register" && role === "provider" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Company</Label>
                <Input data-testid="auth-company" value={form.company} onChange={set("company")} className="mt-1.5 rounded-xl" placeholder="GreenFlow Landcare & Irrigation" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Email</Label>
              <Input data-testid="auth-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 rounded-xl" placeholder="you@email.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Password</Label>
                {mode === "login" && (
                  <button type="button" data-testid="forgot-link" onClick={() => { setMode("forgot"); setError(""); setForgotSent(false); }} className="text-xs text-emerald-700 hover:text-emerald-900">Forgot password?</button>
                )}
              </div>
              <Input data-testid="auth-password" type="password" value={form.password} onChange={set("password")} required className="mt-1.5 rounded-xl" placeholder="••••••••" />
            </div>
            {mode === "register" && (
              <label data-testid="newsletter-optin" className="flex items-start gap-2.5 cursor-pointer select-none">
                <input type="checkbox" checked={form.newsletter_opt_in} onChange={(e) => setForm({ ...form, newsletter_opt_in: e.target.checked })} className="mt-0.5 w-4 h-4 accent-emerald-700" />
                <span className="text-sm text-stone-600">Email me PlantFlow seasonal tips, product updates &amp; landcare stories.</span>
              </label>
            )}
            {error && <p data-testid="auth-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="auth-submit" type="submit" disabled={busy} className="w-full rounded-full h-12 bg-emerald-900 hover:bg-emerald-800 text-white">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="mt-4">
            <div className="relative flex items-center gap-3 my-3">
              <div className="h-px flex-1 bg-stone-200" /><span className="text-xs text-stone-400">or</span><div className="h-px flex-1 bg-stone-200" />
            </div>
            <Button type="button" data-testid="google-signin" variant="outline" onClick={googleSignIn} className="w-full rounded-full h-12">
              <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4 mr-2" /> Continue with Google
            </Button>
          </div>

          <button data-testid="auth-toggle" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="mt-5 text-sm text-stone-500 hover:text-emerald-800 w-full text-center">
            {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
          </button>

          <div className="mt-6 pt-5 border-t border-stone-100">
            <p className="text-[11px] uppercase tracking-[0.2em] text-stone-400 mb-3">Try a demo account</p>
            <div className="grid grid-cols-3 gap-3">
              <Button data-testid="demo-consumer" variant="outline" size="sm" disabled={busy} onClick={() => demo("consumer@arborguild.com", "Consumer@123")} className="rounded-full">Homeowner</Button>
              <Button data-testid="demo-provider" variant="outline" size="sm" disabled={busy} onClick={() => demo("provider@arborguild.com", "Provider@123")} className="rounded-full">Crew</Button>
              <Button data-testid="demo-admin" variant="outline" size="sm" disabled={busy} onClick={() => demo("gsedwards2508@gmail.com", "Admin@123")} className="rounded-full">Admin</Button>
            </div>
          </div>
          </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
